import React, { useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    IconButton,
    Tooltip,
    Collapse,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Filters } from '../types/filters';

interface IdeasFiltersProps {
    filters: Filters;
    onApplyFilters: (filters: {
        genders: number[];
        holidays: number[];
        compilations: number[];
        price_from?: number;
        price_at?: number;
    }) => void;
}

export const IdeasFilters: React.FC<IdeasFiltersProps> = ({ filters, onApplyFilters }) => {
    const [selectedHolidays, setSelectedHolidays] = useState<number[]>([]);
    const [selectedGenders, setSelectedGenders] = useState<number[]>([]);
    const [selectedCompilations, setSelectedCompilations] = useState<number[]>([]);
    const [priceFrom, setPriceFrom] = useState<string>('');
    const [priceAt, setPriceAt] = useState<string>('');
    const [isExpanded, setIsExpanded] = useState(false);

    const handleApplyFilters = () => {
        onApplyFilters({
            holidays: selectedHolidays,
            genders: selectedGenders,
            compilations: selectedCompilations,
            price_from: priceFrom ? Number(priceFrom) : undefined,
            price_at: priceAt ? Number(priceAt) : undefined,
        });
    };

    const handleResetFilters = () => {
        setSelectedHolidays([]);
        setSelectedGenders([]);
        setSelectedCompilations([]);
        setPriceFrom('');
        setPriceAt('');
        onApplyFilters({
            genders: [],
            holidays: [],
            compilations: [],
        });
    };

    const hasActiveFilters = selectedHolidays.length > 0 || 
                           selectedGenders.length > 0 || 
                           selectedCompilations.length > 0 || 
                           priceFrom || 
                           priceAt;

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
            }}>
                <Button
                    startIcon={<FilterListIcon />}
                    onClick={() => setIsExpanded(!isExpanded)}
                    variant={hasActiveFilters ? "contained" : "outlined"}
                    color={hasActiveFilters ? "primary" : "inherit"}
                >
                    Фильтры
                    {hasActiveFilters && (
                        <Chip
                            label={selectedHolidays.length + selectedGenders.length + selectedCompilations.length + (priceFrom ? 1 : 0) + (priceAt ? 1 : 0)}
                            size="small"
                            sx={{ ml: 1, backgroundColor: 'white', color: 'primary.main' }}
                        />
                    )}
                </Button>

                {hasActiveFilters && (
                    <Tooltip title="Сбросить фильтры">
                        <IconButton onClick={handleResetFilters} size="small">
                            <RestartAltIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Collapse in={isExpanded} timeout={300}>
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    mt: 2,
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                    boxShadow: 1
                }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Праздники</InputLabel>
                        <Select
                            multiple
                            value={selectedHolidays}
                            onChange={(e) => setSelectedHolidays(e.target.value as number[])}
                            label="Праздники"
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const holiday = filters.holidays.find(h => h.id === value);
                                        return (
                                            <Chip 
                                                key={value} 
                                                label={holiday?.title} 
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {filters.holidays.map((holiday) => (
                                <MenuItem key={holiday.id} value={holiday.id}>
                                    {holiday.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Пол</InputLabel>
                        <Select
                            multiple
                            value={selectedGenders}
                            onChange={(e) => setSelectedGenders(e.target.value as number[])}
                            label="Пол"
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const gender = filters.genders.find(g => g.id === value);
                                        return (
                                            <Chip 
                                                key={value} 
                                                label={gender?.title} 
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {filters.genders.map((gender) => (
                                <MenuItem key={gender.id} value={gender.id}>
                                    {gender.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Подборки</InputLabel>
                        <Select
                            multiple
                            value={selectedCompilations}
                            onChange={(e) => setSelectedCompilations(e.target.value as number[])}
                            label="Подборки"
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const compilation = filters.compilation.find(c => c.id === value);
                                        return (
                                            <Chip 
                                                key={value} 
                                                label={compilation?.title} 
                                                size="small"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {filters.compilation.map((compilation) => (
                                <MenuItem key={compilation.id} value={compilation.id}>
                                    {compilation.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <TextField
                            label="Цена от"
                            type="number"
                            value={priceFrom}
                            onChange={(e) => setPriceFrom(e.target.value)}
                            size="small"
                            sx={{ width: 120 }}
                        />
                        <TextField
                            label="Цена до"
                            type="number"
                            value={priceAt}
                            onChange={(e) => setPriceAt(e.target.value)}
                            size="small"
                            sx={{ width: 120 }}
                        />
                    </Box>

                    <Button 
                        variant="contained" 
                        onClick={handleApplyFilters}
                        sx={{ height: 40 }}
                    >
                        Применить
                    </Button>
                </Box>
            </Collapse>
        </Box>
    );
};

export default IdeasFilters; 